import { MovementDataRenderer } from '@features/gateCommon';
import { Trans } from '@lingui/macro';
import { Box, Skeleton, Typography } from '@mui/material';
import { type FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { List, RowComponentProps } from 'react-window';
import { PanelPaper } from './common';
import { NotificationCard } from './panelNotification/NotificationCard';
import { NotificationData } from './panelNotification/utils';

interface Props {
  movementId: number | null;
}

const ROW_HEIGHT = 68;

const NotificationRow: FC<
  RowComponentProps<{
    messages: NotificationData[];
    animateFeaturedId: number | null;
    onAnimationDone: () => void;
  }>
> = memo(({ index, style, messages, animateFeaturedId, onAnimationDone }) => {
  const notification = messages[index];
  if (!notification) return null;
  const animate = index === 0 && notification.id === animateFeaturedId;
  return (
    <Box style={style} py={0.5}>
      <NotificationCard
        data={notification}
        isFeatured={index === 0}
        animate={animate}
        onAnimationEnd={onAnimationDone}
      />
    </Box>
  );
});

const NotificationList: FC<{ messages: NotificationData[] }> = ({ messages }) => {
  const lastFeaturedIdRef = useRef<number | null>(null);
  const [animateFeaturedId, setAnimateFeaturedId] = useState<number | null>(null);

  const currentId = messages[0]?.id ?? null;

  useEffect(() => {
    if (currentId !== null && currentId !== lastFeaturedIdRef.current) {
      lastFeaturedIdRef.current = currentId;
      setAnimateFeaturedId(currentId);
    }
  }, [currentId]);

  const onAnimationDone = useCallback(() => setAnimateFeaturedId(null), []);

  const rowProps = useMemo(
    () => ({ messages, animateFeaturedId, onAnimationDone }),
    [messages, animateFeaturedId, onAnimationDone],
  );

  return (
    <List
      rowCount={messages.length}
      rowHeight={ROW_HEIGHT}
      rowComponent={NotificationRow}
      rowProps={rowProps}
      style={{ overflowX: 'hidden' }}
    />
  );
};

export const PanelNotifications: FC<Props> = ({ movementId }) => {
  return (
    <PanelPaper
      sx={{
        gridArea: 'notif',
        flexGrow: 1,
        py: 2,
        pb: 0,
        color: 'white',
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ mb: 1, px: 1 }}>
        <Typography variant="h6" fontWeight="bold">
          <Trans>Operación en tiempo real</Trans>
        </Typography>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {movementId ? (
          <MovementDataRenderer
            movementId={movementId}
            render={({ movement }) => {
              const messages = movement.messages;
              if (messages.length === 0) {
                return (
                  <Typography color="text.secondary" p={2} fontStyle="italic">
                    <Trans>Aún no se han recibido mensajes de la compuerta</Trans>
                  </Typography>
                );
              }
              return <NotificationList messages={messages} />;
            }}
            fallback={() => <Skeleton />}
          />
        ) : (
          <Typography color="text.secondary" p={2} fontStyle="italic">
            <Trans>Aún no se han recibido mensajes de la compuerta</Trans>
          </Typography>
        )}
      </Box>
    </PanelPaper>
  );
};
